
import React, { useState, useEffect, useRef } from 'react';
import { User, Language } from '../../types';
import AuthComponent from '../../components/Auth/AuthComponent';

interface AuthTemplateProps {
  onLogin: (user: User) => void;
  language: Language;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onSwitchLanguage: (lang: Language) => void;
  onLahjaLoginClick?: () => Promise<void> | void;
}

const FEATURES_SLIDES = [
  {
    id: 'services',
    title: { ar: 'مركز الخدمات المتكامل', en: 'Integrated Services Hub' },
    desc: { ar: 'تصفح وفعل عشرات الخدمات الذكية من الدوبلاج إلى التحليل بضغطة زر.', en: 'Browse and activate dozens of AI services from dubbing to analytics with one click.' },
    visual: 'services_grid'
  },
  {
    id: 'flows',
    title: { ar: 'بناء الأتمتة (Flows)', en: 'Visual Automation Flows' },
    desc: { ar: 'اربط الخدمات ببعضها عبر واجهة سحب وإفلات لبناء مسارات عمل معقدة.', en: 'Connect services together via a drag-and-drop interface to build complex workflows.' },
    visual: 'flow_chart'
  },
  {
    id: 'agent',
    title: { ar: 'الوكيل الذكي', en: 'Smart AI Agents' },
    desc: { ar: 'درب وكيلك الخاص ليتحدث بلهجتك وينفذ مهامك بشكل مستقل.', en: 'Train your own agent to speak your dialect and execute tasks independently.' },
    visual: 'robot_orb'
  }
];

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const AuthTemplate: React.FC<AuthTemplateProps> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobileFormVisible, setIsMobileFormVisible] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const isRtl = props.language === 'ar';

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % FEATURES_SLIDES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isMobileFormVisible || window.innerWidth >= 1024) {
        const handleCredentialResponse = (response: any) => {
          setIsLoading(true);
          const payload = decodeJWT(response.credential);
          if (payload) {
            // Fix: Added missing 'role' property required by User interface
            const user: User = {
              id: payload.sub,
              name: payload.name,
              email: payload.email,
              avatar: payload.picture,
              plan: 'free',
              role: 'user'
            };
            setTimeout(() => {
              props.onLogin(user);
              setIsLoading(false);
            }, 1200);
          }
        };

        if ((window as any).google?.accounts?.id) {
          (window as any).google.accounts.id.initialize({
            client_id: "479558762829-92f794r9anlrs7ilkv52270bt482e3tu.apps.googleusercontent.com",
            callback: handleCredentialResponse,
            auto_select: false
          });

          if (googleBtnRef.current) {
            (window as any).google.accounts.id.renderButton(googleBtnRef.current, {
              theme: "outline",
              size: "large",
              width: "100%",
              text: "signin_with",
              shape: "pill",
              logo_alignment: isRtl ? "right" : "left"
            });
          }
        }
    }
  }, [props.onLogin, isRtl, isMobileFormVisible]);

  const handleLahjaLogin = async () => {
    if (props.onLahjaLoginClick) {
        // تفعيل حالة التحميل فوراً لإعطاء انطباع بالاستجابة السريعة
        setIsLoading(true);
        try {
            await props.onLahjaLoginClick();
        } catch (e) {
            console.error("Login trigger failed", e);
            setIsLoading(false); // إعادة الزر لحالته الطبيعية في حال الفشل
        }
    }
  };

  const renderMockup = (type: string) => {
    switch(type) {
      case 'services_grid':
        return (
          <div className="grid grid-cols-2 gap-3 w-64 opacity-90">
             {[1,2,3,4].map(i => (
               <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10 flex flex-col gap-2">
                  <div className={`w-8 h-8 rounded-lg ${i===1?'bg-emerald-500':i===2?'bg-blue-500':i===3?'bg-purple-500':'bg-amber-500'} flex items-center justify-center text-white text-xs`}>
                    <i className={`fas ${i===1?'fa-microphone':i===2?'fa-newspaper':i===3?'fa-robot':'fa-video'}`}></i>
                  </div>
                  <div className="h-2 w-16 bg-white/20 rounded-full"></div>
               </div>
             ))}
          </div>
        );
      case 'flow_chart':
        return (
          <div className="relative w-72 h-48 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 p-4 flex items-center justify-center">
             <div className="absolute left-4 top-16 w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg z-10"><i className="fas fa-bolt"></i></div>
             <div className="absolute left-16 top-[4.5rem] w-16 h-1 bg-white/20"></div>
             <div className="absolute left-32 top-10 w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-white shadow-lg z-10 animate-bounce"><i className="fas fa-brain"></i></div>
             <div className="absolute right-4 top-16 w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center text-white shadow-lg z-10"><i className="fas fa-paper-plane"></i></div>
          </div>
        );
      case 'robot_orb':
        return (
          <div className="relative w-48 h-48 flex items-center justify-center">
             <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full animate-pulse"></div>
             <div className="w-32 h-32 rounded-full border-2 border-emerald-500/50 flex items-center justify-center bg-black/40 backdrop-blur-xl relative z-10 shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                <i className="fas fa-fingerprint text-5xl text-emerald-400"></i>
             </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <AuthComponent
      isLoading={isLoading}
      googleBtnRef={googleBtnRef}
      featuresSlides={FEATURES_SLIDES}
      currentSlide={currentSlide}
      setCurrentSlide={setCurrentSlide}
      renderMockup={renderMockup}
      isMobileFormVisible={isMobileFormVisible}
      setIsMobileFormVisible={setIsMobileFormVisible}
      language={props.language}
      theme={props.theme}
      onToggleTheme={props.onToggleTheme}
      onSwitchLanguage={props.onSwitchLanguage}
      onLahjaLogin={handleLahjaLogin}
    />
  );
};

export default AuthTemplate;